import { action, autorun, computed, makeObservable, observable, override, reaction, when } from 'mobx';

import AppSettings from '../AppSettings';
import { IApiClient } from '../data/client';
import { AddProductToCartCommand, CalculatePreparingTimeForCodeCommand, GetProductByIdQuery } from '../data/model';
import type {
  BaseModelData,
  DaysRangeData,
  GetProductQueryResponse,
  GetStoreContextQueryResponse,
  ShoppingContext,
} from '../data/model';
import { FabricWarningState } from '../layout/Warnings/FabricWarningState';
import { IWarningLocalStorage } from '../layout/Warnings/WarningLocalStorage';
import { WarningStateBase } from '../layout/Warnings/WarningStateBase';
import { ConfiguratorPageTranslation } from '../localization/SiteTranslation';
import { BasePageState } from '../shared/BasePageState';
import { IShareConfiguratorUrlProvider } from '../shared/BaseShareConfiguratorUrlProvider';
import { AsyncCommand } from '../shared/common';
import type { IMemento, PageMetadata } from '../shared/common';
import { EmbeddedQuery, EmbeddedQueryParser } from '../shared/EmbeddedQueryParser';
import { IEventAggregator } from '../shared/EventAggregator';
import { NumberInput, RangeRule } from '../shared/Form';
import { IImagePreloader } from '../shared/ImagePreloader';
import Logger from '../shared/Logger';
import { ModalState } from '../shared/ModalState';
import { INavigationService } from '../shared/NavigationService';
import { IPdfUrlProvider } from '../shared/PdfUrlProvider';
import { StatusCodes } from '../shared/StatusCodes';
import { StoreUrl } from '../shared/StoreUrl';
import { RunLastTasksScheduler } from '../shared/TasksScheduler';
import { AbsoluteUrl } from '../shared/Url';
import { VisualizationMode } from '../shared/visualization';
import { StoreState } from '../StoreState';
import { CalculatePreparingTimeTask } from './CalculatePreparingTimeTask';
import { TabState } from './components/ConfiguratorTabs/TabState';
import { ProductModelsState } from './components/ProductModels/ProductModelsState';
import { RequestQuoteActionState } from './components/RequestQuoteAction/RequestQuoteActionState';
import { ConfigurationShareState } from './ConfigurationShareState';
import ConfigurationUnitPriceMismatchMigrationMonitor from './ConfigurationUnitPriceMismatchMigrationMonitor';
import { ConfiguratorCoreState } from './ConfiguratorCoreState';
import { ConfigurationSummaryViewedEvent, ConfigurationViewedEvent } from './ConfiguratorPageEvents';
import ConfiguratorPageQueryParser from './ConfiguratorPageQueryParser';
import IConfigurationPageQuery from './IConfigurationPageQuery';
import { IVisualizationState } from './IVisualizationState';
import { Tab } from './Tab';
import { VisualizationHybridState } from './VisualizationHybridState';
import { VisualizationStateFactory } from './VisualizationStateFactory';

export interface IConfiguratorMemento extends IMemento {
  storeContextResponse: GetStoreContextQueryResponse;
  productResponse: GetProductQueryResponse;
  code: string;
  shoppingContext: ShoppingContext;
}

export class ConfiguratorState extends BasePageState<IConfiguratorMemento> {
  private selectionStateSetFirstTime = false;
  private settings: AppSettings;
  private client: IApiClient;
  private navigation: INavigationService;
  private imagePreloader: IImagePreloader;
  shareConfiguratorUrlProvider: IShareConfiguratorUrlProvider;
  private slug: string;

  public isRelativeToTheViewport = true;

  public embeddedQuery: EmbeddedQuery;

  public productModelsState: ProductModelsState;

  productResponse: GetProductQueryResponse;

  proceedToCheckoutCommand: AsyncCommand;
  proceedToEmailConfirmationCommand: AsyncCommand;

  baseModel: BaseModelData;

  @observable quantityInput: NumberInput;
  @observable shoppingCartItemId?: string;

  configurationShareModal: ModalState;

  fabricColorsDisclaimerState: FabricWarningState;
  productNotAvailableInCurrentStoreState: WarningStateBase<string>;
  productInactiveWarningState: WarningStateBase<string>;

  visualization: IVisualizationState;

  quantityMin: number;
  @observable
  quantityMax: number;

  translation: ConfiguratorPageTranslation;

  model: IConfigurationPageQuery;
  storeContextResponse: GetStoreContextQueryResponse;
  shoppingContext: ShoppingContext;

  @observable private internalMetadata: PageMetadata;

  public coreState: ConfiguratorCoreState;

  public shareState: ConfigurationShareState;

  public requestQuoteState: RequestQuoteActionState;

  @observable preparingTime: DaysRangeData;

  private eventAggregator: IEventAggregator;

  private calculatePreparingTimeScheduler = new RunLastTasksScheduler((task: CalculatePreparingTimeTask) =>
    this.calculatePreparingTime(task),
  );

  constructor(
    settings: AppSettings,
    client: IApiClient,
    navigation: INavigationService,
    imagePreloader: IImagePreloader,
    pdfOriginProvider: IPdfUrlProvider,
    shareConfiguratorUrlProvider: IShareConfiguratorUrlProvider,
    slug: string,
    translations: ConfiguratorPageTranslation,
    warningLocalStorage: IWarningLocalStorage,
    eventAggregator: IEventAggregator,
  ) {
    super(translations);
    makeObservable(this);
    this.settings = settings;
    this.client = client;
    this.navigation = navigation;
    this.imagePreloader = imagePreloader;
    this.slug = slug;
    this.internalMetadata = {};
    this.translation = translations;
    this.eventAggregator = eventAggregator;

    this.configurationShareModal = new ModalState();
    this.productNotAvailableInCurrentStoreState = new WarningStateBase<string>();
    this.productInactiveWarningState = new WarningStateBase();
    this.fabricColorsDisclaimerState = new FabricWarningState(warningLocalStorage);

    this.quantityMin = 1;
    this.quantityMax = 50;

    this.quantityInput = new NumberInput();

    this.coreState = new ConfiguratorCoreState(client, translations, eventAggregator);
    this.shareState = new ConfigurationShareState(
      client,
      navigation,
      imagePreloader,
      pdfOriginProvider,
      shareConfiguratorUrlProvider,
      () => ({
        productNumber: this.slug,
        query: this.getQueryState(),
        visualizationMode: this.productResponse.visualization.mode,
        visualizationShots: this.productResponse.visualization.mapping.shots,
      }),
    );
    this.requestQuoteState = new RequestQuoteActionState(
      eventAggregator,
      navigation,
      this.quantityInput,
      this.coreState.code,
    );

    this.populateDataFromQuery();
  }

  @override
  get metadata(): PageMetadata {
    return this.internalMetadata;
  }

  @action
  private populateDataFromQuery() {
    const query = this.navigation.currentUrl.query;
    this.model = ConfiguratorPageQueryParser.toModel(query);
    this.embeddedQuery = EmbeddedQueryParser.toModel(query);
    this.quantityInput.value = this.model.quantity || 1;
    this.shoppingCartItemId = this.model.shoppingCartItemId;
  }

  @action
  private initialize(
    storeContextResponse: GetStoreContextQueryResponse,
    response: GetProductQueryResponse,
    code: string,
    shoppingContext: ShoppingContext,
  ) {
    const { baseModel } = response;
    this.storeContextResponse = storeContextResponse;
    this.productResponse = response;
    this.shoppingContext = shoppingContext;
    this.baseModel = baseModel;
    this.quantityMax = this.baseModel.purchaseQuantityLimit;

    this.setQuantityInput();

    const additionalTabs = [new TabState(Tab.Summary, this.translation.tabs)];
    this.coreState.initialize(
      response,
      code,
      this.model.step,
      additionalTabs,
      this.storeContextResponse.shoppingContext,
    ); // TODO: Check if this is a proper place

    this.shareState.initialize(shoppingContext, storeContextResponse.storeContext);
    this.shareState.setCode(code);

    this.requestQuoteState.initialize(this.baseModel, code, this.storeContextResponse.shoppingContext.currencyCode);

    this.startTrackingConfigurationChanges();
    this.restoreStateFromQuery();
    this.coreState.initializeKindOfFabricCordinators(response);

    this.visualization = new VisualizationStateFactory(this.imagePreloader).create(
      response.visualization,
      this.coreState.selectedComponents,
    );

    this.productModelsState = new ProductModelsState(
      this.client,
      this.navigation,
      this.storeContextResponse.shoppingContext,
      this.baseModel,
      () => this.getQueryState(),
    );

    this.proceedToCheckoutCommand = new AsyncCommand(() => this.proceedToCheckout());
    this.proceedToEmailConfirmationCommand = new AsyncCommand(() => this.proceedToEmailConfirmation());

    this.checkAvailabilityInCurrentStore(response);
    this.checkIfDeactivated(response);

    this.setMetadata();

    this.calculatePreparingTimeOnConfigurationCodeChange();

    reaction(
      () => this.coreState.code,
      () =>
        ConfigurationUnitPriceMismatchMigrationMonitor.monitor(
          this.coreState.unitPriceV2,
          this.coreState.unitPrice,
          this.coreState.code,
        ),
    );

    reaction(
      // TODO: verify if this is the best place
      () => this.coreState.code,
      () => {
        this.shareState.setCode(this.coreState.code);
        this.requestQuoteState.setCode(this.coreState.code);
      },
    );

    reaction(
      () => this.coreState.tabs.isSummaryTabActive,
      (isSummaryTabActive) => {
        if (isSummaryTabActive) {
          this.notifyConfigurationSummaryViewed();
        }
      },
    );

    // TODO: Support for VisualizationMode.Full3D
    if (
      this.visualization.mode === VisualizationMode.Default &&
      this.visualization instanceof VisualizationHybridState
    ) {
      this.shareState.setDownloadImageMode('single');
      const visualization = this.visualization;
      reaction(
        () => visualization.imageUrls,
        () => {
          if (visualization.imageUrls.any()) {
            this.shareState.setDownloadImageUrl(visualization.imageUrls.first());
          }
        },
      );
    }

    this.notifyConfigurationViewed();
  }

  private checkAvailabilityInCurrentStore(response: GetProductQueryResponse) {
    if (!response.baseModel.availableInCurrentStore) {
      const store = this.storeContextResponse.storeContext.store;
      const storeName = store.displayName || store.name;
      this.productNotAvailableInCurrentStoreState.open(storeName);
    }
  }

  private checkIfDeactivated(response: GetProductQueryResponse) {
    if (!response.baseModel.isActive) {
      const reasonForInactivation = response.baseModel.reasonForInactivation;
      this.productInactiveWarningState.open(reasonForInactivation);
    }
  }

  @action
  public async onLoad(store: StoreState) {
    let productResponse = await this.client.send(
      new GetProductByIdQuery({ productId: this.slug, shoppingContext: store.shoppingContext }),
    );

    if (productResponse.redirectToProductId) {
      this.navigation.redirect(
        StoreUrl.configuratorUrl(productResponse.redirectToProductId).toString(),
        StatusCodes.MovedTemporary,
      );
    }

    this.initialize(store.storeResponse, productResponse, '000000', store.shoppingContext);
    await this.coreState.ensureCodeIsInSyncWithSelectedOptionsAndAccessories();
  }

  onLoadAdditionalData() {
    this.startUpdatingVisualizationImages();
  }

  @action
  private setMetadata() {
    const title = this.translations.pageTitleFormat.interpolate([['productName', this.productResponse.baseModel.name]]);
    const description = this.translations.pageDescriptionFormat.interpolate([
      ['productName', this.productResponse.baseModel.name],
      [
        'options',
        this.coreState.selectedOptionVariations.map((x) => `${x.option.name} : ${x.variation.name}`).join(', '),
      ],
      [
        'accessories',
        this.coreState.accessories
          .filter((x) => x.selected)
          .map((x) => `${x.name}`)
          .join(', '),
      ],
    ]);
    const url = AbsoluteUrl.parse(this.settings.baseUrl)
      .append(StoreUrl.configuratorUrl(this.slug, this.getQueryState()))
      .toString();
    const imageUrl = this.shareState.imageUrlForPdf;

    this.internalMetadata = {
      url: url,
      title: title,
      description: description,
      imageUrl: imageUrl,
    };
  }

  @action
  restoreMemento(memento: IConfiguratorMemento) {
    this.initialize(memento.storeContextResponse, memento.productResponse, memento.code, memento.shoppingContext);
  }

  @action
  async proceedToEmailConfirmation() {
    this.navigation.navigateTo(StoreUrl.emailConfirmationUrl({ configurationCode: this.coreState.code }));
  }

  @action
  async proceedToCheckout() {
    await this.addProductToCart();
    this.navigation.navigateTo(StoreUrl.checkoutUrl());
  }

  private async addProductToCart() {
    let configuration = this.getProductConfiguration();
    let command = new AddProductToCartCommand({
      shoppingContext: this.storeContextResponse.shoppingContext,
      productConfiguration: configuration,
    });

    await this.client.send(command);
  }

  @action.bound
  updateQuantity(value: number) {
    this.quantityInput.onChange(value);
  }

  @computed get total() {
    const total = { ...this.coreState.unitPrice };
    total.amount = this.quantityInput.value ? this.quantityInput.value * total.amount : total.amount;
    return total;
  }

  getProductConfiguration(ignoreQuantity: boolean = false) {
    return this.coreState.getProductConfiguration(ignoreQuantity ? 1 : this.quantityInput.value);
  }

  getMemento() {
    const memento: IConfiguratorMemento = {
      storeContextResponse: this.storeContextResponse,
      productResponse: this.productResponse,
      code: this.coreState.code,
      shoppingContext: this.shoppingContext,
    };

    return memento;
  }

  public getQueryState() {
    const configuration = this.getProductConfiguration();
    const pageQuery: IConfigurationPageQuery = {
      options: configuration.options.map((x) => x.id),
      accessories: configuration.accessories.map((x) => x.id),
      step: this.coreState.tabs.activeTab.code,
      quantity: configuration.quantity,
      shoppingCartItemId: this.shoppingCartItemId,
    };

    return pageQuery;
  }

  private startTrackingConfigurationChanges() {
    autorun(() => {
      const pageQuery = this.getQueryState();
      if (!this.selectionStateSetFirstTime) {
        this.selectionStateSetFirstTime = true;
        return;
      }
      const query = ConfiguratorPageQueryParser.toQuery(pageQuery);
      this.navigation.setQuery(query);
    });
  }

  private restoreStateFromQuery() {
    const pageQuery = ConfiguratorPageQueryParser.toModel(this.navigation.currentUrl.query);
    this.coreState.restoreState(this.model.code, pageQuery);
  }

  private startUpdatingVisualizationImages() {
    autorun(async () => {
      const components = this.coreState.selectedComponents;
      this.visualization.setComponents(components);
    });
  }

  private setQuantityInput() {
    const outOfRangeWarning = this.translation.summary.quantityInput.outOfRangeWarning.interpolate([
      ['min', this.quantityMin.toString()],
      ['max', this.quantityMax.toString()],
    ]);

    this.quantityInput
      .withRule(new RangeRule(this.quantityMin, this.quantityMax, outOfRangeWarning))
      .triggerValidationImmediately();
  }

  private calculatePreparingTimeOnConfigurationCodeChange() {
    const { storeId, storeType } = this.storeContextResponse.shoppingContext;
    reaction(
      () => this.coreState.code,
      (code) => this.executeCalculatePreparingTimeTask(storeId, storeType, code),
    );
  }

  private executeCalculatePreparingTimeTask(storeId: string, storeType: string, configurationCode: string) {
    const task = new CalculatePreparingTimeTask(storeId, storeType, configurationCode);
    return this.calculatePreparingTimeScheduler.run(task);
  }

  @action
  private async calculatePreparingTime(task: CalculatePreparingTimeTask) {
    const command = new CalculatePreparingTimeForCodeCommand({
      storeId: task.storeId,
      storeType: task.storeType,
      configurationCode: task.configurationCode,
    });

    try {
      const response = await this.client.send(command);

      if (task.cancelled) {
        return;
      }

      this.updatePreparingTime(response.preparingTimeInWorkingDays);
    } catch (error) {
      Logger.exception('Error occurred while calculating preparing time', error);
      throw error;
    }
  }

  @action
  private updatePreparingTime(value: DaysRangeData) {
    this.preparingTime = value;
  }

  private notifyConfigurationViewed() {
    this.eventAggregator.publish(
      new ConfigurationViewedEvent(this.shoppingContext, this.baseModel, this.coreState.getSelectedFeaturesSnaphost()),
    );
  }

  private async notifyConfigurationSummaryViewed() {
    const configurationCode = await this.getSettledConfigurationCode();

    this.eventAggregator.publish(
      new ConfigurationSummaryViewedEvent(
        this.shoppingContext,
        this.baseModel,
        this.coreState.getSelectedFeaturesSnaphost(),
        configurationCode,
      ),
    );
  }

  /**
   * The code is generated server-side, so it lags the selection by a round trip - opening the summary
   * right after changing an option would otherwise report the previous code. Waits for a generation in
   * flight to settle, and reports no code rather than a stale one when it fails or never arrives.
   */
  private async getSettledConfigurationCode(): Promise<string> {
    try {
      await when(() => !this.coreState.configurationCodeLoading, { timeout: 5000 });
    } catch {
      return '';
    }

    return this.coreState.configurationCodeError ? '' : this.coreState.code;
  }
}
